import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import { fetchBundle, fetchBundles, createBundle, updateBundle } from "../../actions/callrecorder/bundlesActions";
import { fetchUser, fetchUsers, updateUser } from "../../actions/callrecorder/usersActions";

export interface BundleInterface {
  url: string;

  id: number,

  name: string,

  shared_secret: string,
  twilio_service_sid: string | null,
  prod_env: boolean,
  favorite: boolean,

  push_cert_url: string | null;

  team_id: string | null,
  apns_key_id: string | null,
  app_store_connect_api_key_id: string | null,
  app_store_connect_api_issuer_id: string | null,
  app_store_connect_api_private_key_url: string | null,

  platform: 'ios' | 'android',
  firebase_project_id: string | null,
  firebase_client_email: string | null,
  firebase_private_key: string | null,

  created_at: string,
  updated_at: string
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null;

  newParams: BundleInterface | undefined;
  newError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null
};

const adaptor = createEntityAdapter<BundleInterface>();

const slice = createSlice({
  name: 'bundles',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setNewParams: (state, action) => {
      state.newParams = action.payload;
      state.newError  = initialState.newError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchBundles.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchBundles.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.bundles;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchBundles.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchBundle.fulfilled, (state, action) => {
        const bundle = Object.values(action.payload.bundle)[0];
        state.entities[bundle.id] = bundle;
      })

      // #create
      .addCase(createBundle.fulfilled, (state, action) => {
        const bundle = Object.values(action.payload.bundle)[0];
        state.entities[bundle.id] = bundle
      })
      .addCase(createBundle.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #update
      .addCase(updateBundle.fulfilled, (state, action) => {
        const bundle = Object.values(action.payload.bundle)[0];
        state.entities[bundle.id] = bundle;
      })
      .addCase(updateBundle.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // #users — merge bundles extracted from user responses
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.bundles };
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.bundle };
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.bundle };
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setEditError,
  setNewParams,
  setNewError
} = slice.actions;

export const {
  selectById: selectBundleById,
  selectIds: selectBundleIds,
  selectEntities: selectBundleEntities,
}  = adaptor.getSelectors<RootState>(state => state.callrecorder.bundles);
